import { useState, useEffect } from 'react';
import { Button, Header, List, Table } from 'semantic-ui-react';
import { DebounceInput } from 'react-debounce-input';
import { createPortal } from 'react-dom';
import { usePDF, PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { flattenToAppURL } from '@plone/volto/helpers';
import { ShowCalculation } from "table";
import Select from 'react-select';
import moment from 'moment';
import holyoakeLogo from '../selection/Holyoake_logo.png';
import blueBanner from '../selection/BlueBanner.png';
import { AcousticFrequencies } from './acoustic';
import { Footer, offices, styles as pdfstyles, AcousticHighlight } from './document';
import { getTld } from '../siteurl';
import { saveAs } from 'file-saver';
import { isEqual } from 'lodash';
import { trackEvent } from '@eeacms/volto-matomo/utils';
import RobotoRegular from '../fonts/Roboto-Regular.ttf';
import RobotoThin from '../fonts/Roboto-Thin.ttf';

Font.register({
    family: 'Roboto',
    fonts: [
      { format: "truetype",
        fontWeight: "normal",
        src: RobotoRegular },
      { format: "truetype",
        fontWeight: "thin",
        src: RobotoThin }
    ]
  });

Font.register({ fontFamily: 'Helvetica', variants: 'thin' });
Font.register({ fontFamily: 'Helvetica-Bold' });
Font.register({ fontFamily: 'Helvetica-Oblique' });
Font.registerHyphenationCallback(word => (
    [word]
));

const styles = StyleSheet.create({...pdfstyles,
  section: {
      flexGrowx: 1,
      display: 'block',
      borderBottom: '1px solid #0061c6',
      paddingBottom: 9,  
      paddingTop: 3,
      textAlign: 'left',
  },
    /*    Top Header area   */
  h1Head: {
      color: '#ff5500',
      fontFamily: 'Helvetica-Bold',
      fontWeight: 200,
      fontSize: 19,
      paddingBottom: 5,  
      width: '70%',
  },
  normalHead: {
      fontSize: 15,
      paddingBottom: 4,
  }, 
  strongHead: {
      fontFamily: 'Helvetica-Bold',
      fontSize: 15,
      fontWeight: 200,
  },    
    /*    Blue Headings     */
  h2Head: {
      color: '#0061c6',
      fontSize: 12,
      letterSpacing: '-0.5px',
      paddingBottom: 8,
      paddingTop: 5,
      textTransform: 'uppercase',
  },
    /*    Not used     */
 /*  normal: {
      fontSize: 9,
      paddingBottom: 3,
      fontFamily: 'Helvetica-Bold',
  }, */    
    /*    Image in Dimensions area     */
  dimension: {
      fontSize: 10,
      backgroundColor: "white",
      paddingBottom: 2,
      letterSpacing: '-0.5px',
  },    
    /*    List in Notes area     */
  list_element: {
      fontSize: 7.5,
      lineHeight: 1.4,
      marginLeft: 10,
      paddingLeft: 10,
      paddingBottom: 3,
      width: '96%',
  },
    /*    Line string in Project area     */
  project: {
      /* fontFamily: 'Helvetica-Bold', */
      color: '#0061c6',
      fontSize: 9,
      paddingBottom: 1,
      paddingTop: 5,
      letterSpacing: '-0.5px',
      textTransform: 'uppercase',
  },   
  projectnormal: {
      fontSize: 9,
      paddingBottom: 3,
      paddingTop: 5,
      paddingLeft: 5,
  },    
    /*    Not used     */
  /* submittaldate: {
      fontFamily: 'Helvetica-Bold',
      textAlign: 'right', 
      fontSize: 9,
      paddingBottom: 2,
      marginLeft: -24,
      letterSpacing: '-0.5px',
  }, */   
  linestring: {
      fontSize: 8,
      letterSpacing: '-0.5px',
      paddingTop: 5,
  },    
});

function Acoustic({ record }){
    const calculator = record?.submittal;
    let borderv = '1px solid #e1f0ff';
    const columns = AcousticFrequencies( { acoustic: calculator?.acoustic } );
    if ( calculator?.acoustic?.items.length > 0 ){
        return (
            <View style={{ marginTop: 0 }}>
                <View styles={ styles.acoustic } style={{ flexDirection: 'row', justifyContent: 'space-between', flexGrow: 0, display: 'flex', border: '1px solid #0061c6' }}>
                    <View key="frequency" style={{ flexDirection: 'column', width: "46%", textAlign: 'center' }} >
                        <Text>Frequency</Text>
                    </View>
                    { calculator?.acoustic?.items.map((item) => (
                        <View key={item["identifier"]} style={{ flexDirection: 'column', width: "60%", textAlign: 'center' }} >
                             <Text>{ item["identifier"] } (dB)</Text>
                        </View>
                    ))}
                </View>
                <Text style={{ paddingTop: 2 }} ></Text>
                {columns.map((column) => (
                    <View key={ column } styles={ styles.acoustic } style={{ flexDirection: 'row', justifyContent: 'space-around', flexGrow: 0, borderLeft: borderv, borderRight: borderv }}>
                        <View style={{ flexDirection: 'column', width: "46%", textAlign: 'right', paddingRight: 25, borderBottom: borderv }} >
                            <Text>{ column }</Text> 
                        </View>
                        { calculator?.acoustic?.items.map((item, index) => (
                            <View key={ item["@id"] + index } style={{ flexDirection: 'column', width: "60%", textAlign: 'right', paddingRight: 46, borderBottom: borderv, borderLeft: borderv }} >
                              {(item['identifier'] === 'Insertion Loss') 
                               ? (<AcousticHighlight value={item[column]} best={record?.acoustic?.acoustic_max?.[column]} performance={record?.acoustic?.acoustic_insertion?.[column]} units="test" />)
                               :(<Text>{item[column]}</Text>) }
                            </View>
                        ))}
                    </View>
                ))}
            </View>
      )
    } else { return null;
 }
}


function Project({ title, value }){
    return (
        <View key={title} style={{ flexDirection: 'row', justifyContent: 'space-around', flexGrow: 0, paddingBottom: 4, borderBottom: '1px solid #0061c6' }}>
            <View key="title" style={{ ...styles.project, flexDirection: 'column', width: "50%", fontWeight: 700, paddingLeft: "0px", lineHeight: 1.6 }} >
                <Text>{ title }</Text>
              </View>
            <View key="value" style={{ fontSize: 9, flexDirection: 'column', width: "50%", fontWeight: 100, paddingTop: 2, textAlign: 'left' }} >
                  <Text>{ value }</Text>
              </View>
          </View>
    )};

/*  default values are for construction (3 columns)  -  dimensions can have 2, 3 or 4 column combinations  */
export function Formatted({ title, value, columns, column, warning, best}){
    let colourise = {...styles?.[warning] || {}};
    if (title === 'Pressure Drop' && best && best >= value.replace(/ Pa/, '')){
        colourise = {...colourise,
                     ...styles.best};
    }
    let widtht = '44%';
    let widthv = '48%';
    let marginlt = 0;
    let marginlv = 0;
    let font = 8.5;
    if (columns == 4){
        if (column == 0) {
            widtht = '33%';
            widthv = '20%';
        } 
        else if (column == 3) {
            widtht = '33%';
            widthv = '20%';
        }
        else if (column == 1) {
            widtht = '40%';
            widthv = '26%';
        }
        else {
            widtht = '41%';
            widthv = '15%';
        }
        font = 9;
    }
    else if (columns == 3){
        widtht = '42%';
        widthv = '24%';
        font = 9;
    }
    else if (columns == 2){
        widtht = '26%';
        widthv = '28%';
        font = 9;
    }
    return (
        <View styles={styles.performance} style={{ flexDirection: 'row' }}>
            <View style={{ flexDirection: 'column', display: 'flex', width: widtht, fontFamily: 'Helvetica-Bold', fontSize: font, lineHeight: 1.6, justifyContent: 'space-between' }} >
                  <Text>{ title }{ (title != '') ? ':' : 'X'}</Text>
              </View>
            <View style={{ flexDirection: 'column', display: 'flex', width: widthv, fontWeight: 100, lineHeight: 1.6, fontSize: font, justifyContent: 'space-between', ...colourise }} >
                  <Text>{ value }</Text>
              </View>
          </View>
    )};
    
export function Description({ title, value }) {
    return (
             <View key="outlet" style={{ flexDirection: 'row' }} >
                <Text key="name" style={ styles?.project }>{title}</Text>
                 <Text key="name" style={ styles?.projectnormal }>{value}</Text>
             </View>
    )};

export function ProductPDF({ selection, type }) {
  const date = new Date().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'})
  const yyyymmdd = new Date().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric'})
  const searchParams = new URLSearchParams(document.location.search)
  if (searchParams.get('debug')) {
      console.log("Product PDF", selection);
  }
  const title = 'submittal';

  return (
    <Document title={title} author="HolyoakeByPrice">
      <Page size="A4" style={styles.page}>
          <View>
            <Image src={holyoakeLogo} style={styles.logo} />
          </View>
          <View style={{...styles.h1, fontSize: 42, height: 'auto', paddingTop: 150 }}>
              <View key='center' style={{ flexDirection: 'row', display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', marginLeft: '35%' }} >
             <Text>Noise Control Submittals</Text>
         </View>
      </View>
      <View style={{...styles.section, height: 'auto', paddingTop: 400, fontSize: 28, borderBottom: 0 }}>
          <Description title="Date Generated:" value={yyyymmdd} />
          <Description title="Project Name:" value={ selection.project.project_name} />
      </View>
        <Footer country={ getTld() }
                message={ 'Information within this document is subject to change without notice' } />
      </Page> 

      { selection.items['silencer'].map((item, index) => (
      <Page key={'page' + index} size="A4" style={styles.page}>
          <View key="header" style={styles.section}>
            <Image src={holyoakeLogo} style={styles.logo} />
            <Text style={ styles.h1Head }>{ item?.model }</Text>
            <Text style={ styles.normalHead }>{ item?.submittal?.description }</Text>
            <Text style={ styles.normalHead }>
                <Text style={ styles.strongHead }>Reference: </Text>
                { item?.reference }</Text>
          </View>
          <View style={{...styles.section, height: 'auto' }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex', textAlign: 'left', lineHeight: 1.3 }}>
            { item?.submittal?.columns.map((fields, index) =>
            <View key={item?.submittal?.headings[index]} style={{ flexDirection: 'column' }} >
                <Text key={item?.submittal?.headings[index] || 'unknown' }
                      style={{...styles?.h2Head }}>{item?.submittal?.headings[index] || ' ' }</Text>
               
                    { fields.map(([field, title]) => {
                     let value = item?.submittal?.fields?.[field] || item?.[field];
                     if (field == 'overall_width' && item?.submittal?.fields?.extended_width){
                         value = value + ' (' + item?.submittal?.fields?.extended_width + ')';
                     }
                     if (value !== undefined){
                         return (<Formatted key={item?.submittal?.headings[index] + field}
                                            title={ title }
                                            value={ value }
                                            columns={ item?.submittal?.columns.length }
                                            column={ index } />)
                     }})
                }
             </View>
            )}
          </View>                
              { Object.entries(item?.submittal?.positions?.offsets).flatMap(
                    ([key, values]) => { return ( 
                        values.map(value => { 
                        return (<Text key={key + '_' + value['top'] + '_' + value['left']} style={{ ...styles.dimension, position: "absolute", top: value['top'], left: value['left'], zIndex:10 }}>{ item?.submittal?.fields[key] }</Text>)}))})}
              <Image src={flattenToAppURL(`++api++/${item?.submittal?.path}/@@submittal_image/${item?.submittal?.positions?.image}`,)} style={{ position: "relative", bottom: 0, width: '100%', height: 'auto', minHeight: 175, maxHeight: 175, zIndex: 20 }} /> 
              <Text style={{ ...styles.discrete, fontSize: 7 }} >Images are generic representations of and not to scale. The actual configuration may not be shown.</Text>
      </View>
          
      <View style={{...styles.section }} > 
          <View style={{ flexDirection: 'row' }}>
              <View style={{ flexDirection: 'column', display: 'flex' }} >
                <Text style={ styles?.h2Head }>Performance</Text>

                  <View style={{ lineHeight: 1.6, width: "240", display: 'flex' }} >
                 <Formatted title="Airflow" value={item?.flow_rate} />
                 <Formatted title="Face Velocity" value={item?.submittal?.fields?.face_velocity} />
                 <Formatted title="Air Direction" value={item?.submittal?.fields?.direction} />
                 <Formatted title="Pressure Drop" value={item?.submittal?.fields?.pressure_drop}
                                                  warning={item?.submittal?.warnings?.pressure_drop}
                                                  best={item?.submittal?.pressure_drop_min} />
                 <Formatted title="Installed Pressure Drop" value={item?.submittal?.fields?.installed_pressure_drop} />
               </View>
             </View>
             <View style={{ flexDirection: 'column', whiteSpace: 'wrap', width: "60%", fontSize: 10, fontWeight: 100, color: '#4a4b4c', lineHeight: 1.2, marginLeft: 20, paddingLeft: 20, paddingRight: 0, paddingTop: 4 }} >
                <Acoustic record={item} />
            </View>
          </View>
      </View>
      <View style={styles.section}>
          <Text style={ styles?.h2Head }>Construction</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex', fontSize: 8, textAlign: 'left' }}>
              <View style={{ flexDirection: 'column', width: '450'  }} >
                 <Formatted title="Casing" value="22 GA Galvanised" />
                 <Formatted title="Perforated Liner" value="22 GA Galvanised" />
             </View>
              <View style={{ flexDirection: 'column', width: '450' }} >
                 <Formatted title="Insulation Media" value={ item?.submittal?.fields?.insulation_type } />
                 <Formatted title="Inlet Condition" value={ item?.submittal?.fields?.inlet_condition } />
                 <Formatted title="Inlet Connection" value={ item?.submittal?.fields?.inlet_connection } />
             </View>
              <View style={{ flexDirection: 'column', width: '450' }} >
                 <Formatted title="Media Protection" value={ item?.submittal?.fields?.media_protection } />
                 <Formatted title="Outlet Condition" value={ item?.submittal?.fields?.outlet_condition } />
                 <Formatted title="Outlet Connection" value={ item?.submittal?.fields?.outlet_connection } />
             </View>
          </View>
      </View>
          <View style={{...styles.section, minHeight: 120 }}>
          <Text style={ styles?.h2Head }>Notes</Text>
          { item?.submittal?.notes.map((line, index) => (
              <View key={index} style={{ ...styles.list_element, flexDirection: 'row' }} >
              <Text> • </Text><Text>{ line }</Text>
              </View>
          ))}
      </View>
      <View style={ styles.section }>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex' }} >
             <View style={{ flexDirection: 'row' }} >
                <Description title="Project:" value={ selection.project.project_name} />
             </View>
              <View style={{ flexDirection: 'row' }} >
                <Description title="Submittal Date:" value={yyyymmdd} />
             </View>
           </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', display: 'flex', marginTop: -4 }} >
             <View style={{ flexDirection: 'row' }} >
                <Description title="Description:" value={ item?.submittal?.description } />
             </View>
              <View style={{ flexDirection: 'row' }} >
                 <Text style={{ ...styles?.linestring }}>{item?.submittal?.linestring}</Text>
             </View>
           </View>
      </View>
        <Footer country={ getTld() } message={ 'Information within this document is subject to change without notice' } />
      </Page> ))}

      <Page size="A4" style={styles.page}>
          <View>
            <Image src={holyoakeLogo} style={styles.logo} />
          </View>
          <Image src={flattenToAppURL(`/noise-control/accreditation-certificate.png/@@images/image/huge`,)}
                 style={{position: 'absolute', top: 0, left: 0, width: '107%', height: '103%' }} />
        <Footer country={ getTld() } message={ 'Information within this document is subject to change without notice' } />
      </Page> 
    </Document>
  );
};

export function DownloadPdfButton ({ selection, disabled, setDisabled, onClose }) {
    const [load, setLoad] = useState(false);
    const type = 'silencer';
    const name = 'submittal';
    let PDF = ProductPDF;
    const mydoc = (<PDF selection={selection}
                        type={type}
                    />); 

    function download(){
        trackEvent({category: 'pdf', action: "download", name: name});
        onClose()
    }

    const filename = [selection?.project?.project_name,
                      selection?.project?.reference,
                      type,
                      name].filter(item => (item)).reduce((prev, curr) => prev + '-' + curr).replace(/ /g, '-') + ".pdf";

    return (
     <PDFDownloadLink className={"ui button add " + (load ? 'yellow' : 'green')} 
              document={ mydoc }
              fileName={ filename }
              onClick={download}
              style={{pointerEvents: ((disabled || load) ? 'none' : 'auto')}}
              >
         {({ blob, url, loading, error }) => (
             (useEffect(() => {
                  if (loading){
                      trackEvent({category: 'pdf', action: "generate", name: name});
                  }
                  setLoad(loading);
                  setDisabled(loading);
             }, [loading]) || loading) ? 'Generating Submittal PDF...' : 'Download Submittal PDF')
      }
    </PDFDownloadLink>
    )
}

function DownloadPdfForm({ selection, setSelection, onClose }) {
    const [disabled, setDisabled] = useState(true);

    const handleFieldChange = (event) => {
        const { name, value } = event.target;
        const project = {...selection.project};
        project[name] = value;
        if ( isEqual(project, selection.project)){
            setDisabled(false);
        } else {
            setDisabled(true);
            setSelection({...selection,
                          project: {...project}});
        }
    };
    const searchParams = new URLSearchParams(document.location.search)
    if (searchParams.get('debug')) {
        console.log("DownloadPdfForm", selection);
    }

    return (
        <div className="ui page modals dimmer transition visible active pdfform">
            <div className="ui modal transition visible active modal pdf">
                <div className="actions">
                    <Button className="ui basic circular floated button orange" aria-label="Close" title="close" onClick={onClose}>Close</Button>
                </div>
                <div className="ui header">
                    <h1 className="center">Submittal</h1>
                </div>
                <form className="project">
                    <Table className="pdfform">
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell className="blue text">Project Name:</Table.Cell>
                                <Table.Cell> <DebounceInput debounceTimeout={500} type="text" placeholder="Optional"  name="project_name" value={selection?.project?.project_name} onChange={(event)=>handleFieldChange(event)} className="project" /></Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell colSpan="2"> 
                <DownloadPdfButton
                    selection={selection}
                    disabled={disabled}
                    setDisabled={setDisabled}
                    onClose={onClose}
                />
                                </Table.Cell> 
                           </Table.Row>
                    </Table.Body>
                </Table>
                </form>
            </div>
        </div>
       )
}


export function GeneratePdfSubmittal ({ selection, setSelection }) {
  const [showModal, setShowModal] = useState(false)
  const [documentGenerated, setdocumentGenerated] = useState(false);
  return (
        <>
            <Button className="ui button add blue"
                onClick={() => setShowModal(true) }>Create Submittal PDF</Button>
            {showModal && createPortal(

                <DownloadPdfForm selection={selection}
                                 setSelection={setSelection}
                                 onClose={() => setShowModal(false)} />,

                document.body
            )}
        </>
    )
}
